import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight, ArrowUpRight, Quote } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Editorial category cards - 8 categories
const CATEGORIES = [
  { name: 'Her Money', tag: '01', desc: 'Money, wealth, income, financial independence and women\'s economics.', color: 'bg-burgundy', textColor: 'text-ivory' },
  { name: 'Her Work', tag: '02', desc: 'Career, entrepreneurship, leadership and professional life.', color: 'bg-beige', textColor: 'text-charcoal' },
  { name: 'Her Mind', tag: '03', desc: 'Education, learning, ideas, knowledge and intellectual growth.', color: 'bg-terracotta', textColor: 'text-ivory' },
  { name: 'Her Voice', tag: '04', desc: 'Stories, opinions, conversations and experiences.', color: 'bg-ivory-warm', textColor: 'text-charcoal' },
  { name: 'Her Relationships', tag: '05', desc: 'Family, friendship, love, marriage and social relationships.', color: 'bg-plum', textColor: 'text-ivory' },
  { name: 'Her Body', tag: '06', desc: 'Health, identity, body, wellbeing and self-understanding.', color: 'bg-rose-muted', textColor: 'text-ivory' },
  { name: 'Her World', tag: '07', desc: 'Society, culture, economics, politics and global women\'s issues.', color: 'bg-charcoal', textColor: 'text-ivory' },
  { name: 'Her Future', tag: '08', desc: 'Technology, education, opportunities, ambition and the future.', color: 'bg-gold-warm', textColor: 'text-charcoal' }
];

// Sample stats for Research section
const RESEARCH_STATS = [
  { number: '1 in 3', label: 'Women globally will experience gender-based inequality in the workplace.', source: 'ILO, 2024' },
  { number: '$172T', label: 'Global GDP if women participated equally in the labour force.', source: 'World Economic Forum' },
  { number: '132 years', label: 'Current projection to close the global gender gap at present pace.', source: 'WEF Global Gender Gap Report' }
];

// Sample women profiles
const WOMEN_PROFILES = [
  {
    name: 'Priya Sharma',
    role: 'Software Architect',
    location: 'Bangalore, India',
    quote: 'I built my career one line of code at a time, but the sisterhood built me.',
    image: 'https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=800&auto=format&fit=crop'
  },
  {
    name: 'Amara Okafor',
    role: 'Social Entrepreneur',
    location: 'Lagos, Nigeria',
    quote: 'When one woman rises, she brings her whole community with her.',
    image: 'https://images.unsplash.com/photo-1531123897727-8f129e1688ce?w=800&auto=format&fit=crop'
  },
  {
    name: 'Elena Rossi',
    role: 'Independent Filmmaker',
    location: 'Milan, Italy',
    quote: 'Cinema is how I tell the stories the world tries to erase.',
    image: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?w=800&auto=format&fit=crop'
  }
];

// Sample community questions
const QUESTIONS = [
  { q: 'How did you negotiate your first raise?', category: 'Her Work', responses: 42 },
  { q: 'Is it possible to build wealth on a modest income?', category: 'Her Money', responses: 68 },
  { q: 'How do you balance a demanding career and motherhood?', category: 'Her Relationships', responses: 91 },
  { q: 'What does financial independence mean to you?', category: 'Her Money', responses: 55 }
];

// Sample opportunities
const OPPORTUNITIES = [
  { title: 'Chevening Scholarships 2026', type: 'Scholarship', deadline: 'March 15', location: 'Global' },
  { title: 'Women in Tech Fellowship', type: 'Fellowship', deadline: 'April 30', location: 'Remote' },
  { title: 'Rise Grant for Female Founders', type: 'Grant', deadline: 'May 1', location: 'Global' }
];

const Homepage = () => {
  const [articles, setArticles] = useState([]);
  const [featured, setFeatured] = useState(null);

  useEffect(() => {
    const fetchArticles = async () => {
      try {
        const res = await axios.get(`${API}/articles?limit=6`);
        setArticles(res.data);
        if (res.data.length > 0) setFeatured(res.data[0]);
      } catch (error) {
        console.error(error);
      }
    };
    fetchArticles();
  }, []);

  return (
    <div className="bg-ivory">
      {/* HERO SECTION */}
      <section className="relative py-20 lg:py-32 px-4 sm:px-6 lg:px-8" data-testid="hero-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7 space-y-8 animate-fade-up">
              <p className="label-editorial" data-testid="hero-eyebrow">A GLOBAL WOMEN'S PLATFORM</p>
              <h1 className="font-serif text-5xl md:text-6xl lg:text-8xl leading-[0.95] text-charcoal" data-testid="hero-title">
                Sisters<br />
                <em className="text-burgundy font-normal not-italic">Empowering</em><br />
                Sisters.
              </h1>
              <p className="text-lg md:text-xl text-charcoal-muted max-w-xl leading-relaxed" data-testid="hero-subtitle">
                A global space for women to share, learn, question, connect and grow — together.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 pt-4">
                <Link to="/register" className="btn-burgundy px-8 py-4 inline-flex items-center justify-center gap-2 group" data-testid="hero-join-button">
                  Join the Sisterhood
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Link>
                <Link to="/community" className="btn-outline-charcoal px-8 py-4 inline-flex items-center justify-center gap-2" data-testid="hero-explore-button">
                  Explore Behenchara
                </Link>
              </div>
            </div>
            <div className="lg:col-span-5 animate-fade-up stagger-2">
              <div className="aspect-[4/5] relative overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1531123897727-8f129e1688ce?w=1200&auto=format&fit=crop"
                  alt="Diverse women together"
                  className="w-full h-full object-cover"
                  data-testid="hero-image"
                />
                <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-charcoal to-transparent">
                  <p className="text-xs text-ivory uppercase tracking-widest font-mono">From every corner of the world</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WHY BEHENCHARA? */}
      <section className="py-20 lg:py-28 px-4 sm:px-6 lg:px-8 border-t border-subtle" data-testid="why-section">
        <div className="max-w-4xl mx-auto text-center space-y-8">
          <p className="label-editorial">Why Behenchara?</p>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-tight text-charcoal">
            Because every woman deserves a place where she can <em className="text-burgundy not-italic font-normal">speak</em>, <em className="text-burgundy not-italic font-normal">learn</em>, ask questions, discover opportunities and find women who understand.
          </h2>
          <div className="hairline w-24 mx-auto"></div>
          <p className="text-lg text-charcoal-muted leading-relaxed max-w-2xl mx-auto">
            Behenchara is where women share their stories, discover ideas, ask questions, find opportunities, learn together, and build a community that spans continents and generations.
          </p>
        </div>
      </section>

      {/* EXPLORE HER WORLD */}
      <section className="py-20 lg:py-28 px-4 sm:px-6 lg:px-8 border-t border-subtle" data-testid="explore-her-world-section">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-16 gap-6">
            <div>
              <p className="label-editorial mb-4">Explore Her World</p>
              <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-tight text-charcoal">Eight worlds.<br/>One sisterhood.</h2>
            </div>
            <Link to="/articles" className="label-editorial hover:text-terracotta inline-flex items-center gap-2 group">
              View all categories <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
            </Link>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-0 border border-subtle">
            {CATEGORIES.map((cat) => (
              <Link
                key={cat.name}
                to={`/articles?category=${cat.name}`}
                className={`editorial-card ${cat.color} ${cat.textColor} p-8 min-h-[280px] flex flex-col justify-between border border-subtle -m-px`}
                data-testid={`category-card-${cat.name.toLowerCase().replace(' ', '-')}`}
              >
                <div>
                  <p className="text-xs font-mono opacity-60 mb-6">— {cat.tag}</p>
                  <h3 className="font-serif text-3xl mb-4">{cat.name}</h3>
                  <p className="text-sm opacity-80 leading-relaxed">{cat.desc}</p>
                </div>
                <ArrowUpRight className="h-5 w-5 mt-6 opacity-60" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* THE BEHENCHARA MAGAZINE */}
      <section className="py-20 lg:py-28 px-4 sm:px-6 lg:px-8 bg-ivory-warm border-t border-subtle" data-testid="magazine-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="label-editorial mb-4">The Behenchara Magazine</p>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-tight text-charcoal">Essays. Interviews.<br/>Ideas worth reading.</h2>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Featured large card */}
            {featured && (
              <Link to={`/articles/${featured.slug}`} className="lg:col-span-2 editorial-card group" data-testid="magazine-featured-article">
                <div className="aspect-[16/10] overflow-hidden mb-6">
                  <img src={featured.cover_image || 'https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=1200&auto=format&fit=crop'} alt={featured.title} className="w-full h-full object-cover" />
                </div>
                <p className="label-editorial mb-3">{featured.category} · {featured.reading_time} min read</p>
                <h3 className="font-serif text-3xl md:text-4xl text-charcoal mb-4 leading-tight group-hover:text-burgundy transition-colors">{featured.title}</h3>
                <p className="text-charcoal-muted leading-relaxed line-clamp-3 mb-4">{featured.excerpt}</p>
                <p className="text-sm text-muted-warm font-mono">By {featured.author_name}</p>
              </Link>
            )}

            {/* Side articles */}
            <div className="space-y-8">
              {articles.slice(1, 4).map((article) => (
                <Link key={article.article_id} to={`/articles/${article.slug}`} className="editorial-card group block pb-8 border-b border-subtle last:border-b-0" data-testid={`magazine-article-${article.article_id}`}>
                  <p className="label-editorial mb-2">{article.category}</p>
                  <h4 className="font-serif text-xl text-charcoal mb-2 leading-snug group-hover:text-burgundy transition-colors">{article.title}</h4>
                  <p className="text-sm text-charcoal-muted line-clamp-2 mb-2">{article.excerpt}</p>
                  <p className="text-xs text-muted-warm font-mono">{article.author_name} · {article.reading_time} min</p>
                </Link>
              ))}
            </div>
          </div>

          <div className="text-center mt-16">
            <Link to="/articles" className="btn-outline-charcoal px-8 py-4 inline-flex items-center gap-2" data-testid="magazine-view-all">
              Read the Magazine <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* YOUR STORY MATTERS */}
      <section className="py-20 lg:py-32 px-4 sm:px-6 lg:px-8 bg-burgundy text-ivory" data-testid="your-story-section">
        <div className="max-w-5xl mx-auto text-center space-y-8">
          <Quote className="h-12 w-12 text-terracotta mx-auto" />
          <p className="label-editorial text-terracotta">Your Story Matters</p>
          <h2 className="font-serif text-5xl md:text-6xl lg:text-7xl leading-tight">
            Every woman carries a story.<br/>
            <em className="not-italic text-terracotta">Tell us yours.</em>
          </h2>
          <p className="text-lg text-ivory/80 max-w-2xl mx-auto leading-relaxed">
            Personal experiences, opinions, essays, questions, lessons learned — share them publicly, anonymously, or privately for editorial consideration.
          </p>
          <Link to="/submit-story" className="inline-flex items-center gap-2 px-10 py-5 bg-ivory text-charcoal hover:bg-beige transition-colors font-medium text-sm uppercase tracking-widest" data-testid="share-story-button">
            Share Your Story <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      {/* ASK THE SISTERHOOD */}
      <section className="py-20 lg:py-28 px-4 sm:px-6 lg:px-8 border-t border-subtle" data-testid="ask-sisterhood-section">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-16 gap-6">
            <div>
              <p className="label-editorial mb-4">Ask the Sisterhood</p>
              <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-tight text-charcoal">Questions.<br/>Answered by women, for women.</h2>
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-0 border border-subtle">
            {QUESTIONS.map((q, i) => (
              <div key={i} className="p-8 border border-subtle -m-px bg-white hover:bg-ivory-warm transition-colors cursor-pointer editorial-card" data-testid={`question-card-${i}`}>
                <p className="label-editorial mb-4">{q.category}</p>
                <h3 className="font-serif text-2xl text-charcoal mb-4 leading-snug">{q.q}</h3>
                <p className="text-sm text-muted-warm font-mono">{q.responses} responses from the sisterhood</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BEHENCHARA RESEARCH */}
      <section className="py-20 lg:py-28 px-4 sm:px-6 lg:px-8 bg-charcoal text-ivory" data-testid="research-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="label-editorial text-terracotta mb-4">Behenchara Research</p>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-tight">Data. Knowledge.<br/>Global gender insights.</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-0 border border-charcoal-muted">
            {RESEARCH_STATS.map((stat, i) => (
              <div key={i} className="p-10 border border-charcoal-muted -m-px" data-testid={`research-stat-${i}`}>
                <p className="font-serif text-6xl md:text-7xl text-terracotta mb-4">{stat.number}</p>
                <p className="text-ivory/80 text-base leading-relaxed mb-4">{stat.label}</p>
                <p className="text-xs font-mono uppercase tracking-widest text-ivory/50">— {stat.source}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WOMEN OF BEHENCHARA */}
      <section className="py-20 lg:py-28 px-4 sm:px-6 lg:px-8 border-t border-subtle" data-testid="women-of-behenchara-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="label-editorial mb-4">Women of Behenchara</p>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-tight text-charcoal">Real women.<br/>Real journeys.</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {WOMEN_PROFILES.map((profile, i) => (
              <div key={i} className="editorial-card group" data-testid={`profile-${i}`}>
                <div className="aspect-[4/5] overflow-hidden mb-6">
                  <img src={profile.image} alt={profile.name} className="w-full h-full object-cover" />
                </div>
                <p className="label-editorial mb-2">{profile.location}</p>
                <h3 className="font-serif text-2xl text-charcoal mb-1">{profile.name}</h3>
                <p className="text-sm text-muted-warm mb-4 font-mono">{profile.role}</p>
                <p className="text-charcoal-muted italic leading-relaxed">"{profile.quote}"</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* OPPORTUNITIES FOR HER */}
      <section className="py-20 lg:py-28 px-4 sm:px-6 lg:px-8 bg-ivory-warm border-t border-subtle" data-testid="opportunities-section">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-16 gap-6">
            <div>
              <p className="label-editorial mb-4">Opportunities For Her</p>
              <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-tight text-charcoal">Scholarships.<br/>Grants. Fellowships.</h2>
            </div>
            <Link to="/careers" className="label-editorial hover:text-terracotta inline-flex items-center gap-2">
              View all opportunities <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="space-y-0 border border-subtle bg-white">
            {OPPORTUNITIES.map((opp, i) => (
              <Link to="/careers" key={i} className="flex flex-col md:flex-row md:items-center justify-between p-6 md:p-8 border-b border-subtle last:border-b-0 hover:bg-ivory-warm transition-colors group" data-testid={`opportunity-${i}`}>
                <div className="flex-1">
                  <p className="label-editorial mb-2">{opp.type}</p>
                  <h3 className="font-serif text-2xl text-charcoal group-hover:text-burgundy transition-colors">{opp.title}</h3>
                </div>
                <div className="flex items-center gap-8 mt-4 md:mt-0">
                  <div className="text-right">
                    <p className="text-xs text-muted-warm font-mono uppercase tracking-widest">Deadline</p>
                    <p className="font-serif text-lg text-charcoal">{opp.deadline}</p>
                  </div>
                  <ArrowRight className="h-5 w-5 text-charcoal group-hover:text-burgundy transition-colors" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* WOMEN, EVERYWHERE */}
      <section className="py-20 lg:py-28 px-4 sm:px-6 lg:px-8 border-t border-subtle" data-testid="women-everywhere-section">
        <div className="max-w-6xl mx-auto text-center">
          <p className="label-editorial mb-4">Women, Everywhere.</p>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-tight text-charcoal mb-12">From every corner of the world.</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-0 border border-subtle">
            {['India · South Asia', 'Africa · Middle East', 'Europe · Americas', 'Southeast Asia · Oceania'].map((region, i) => (
              <div key={i} className="p-8 border border-subtle -m-px hover:bg-ivory-warm transition-colors">
                <p className="text-xs font-mono uppercase tracking-widest text-terracotta mb-2">Region 0{i + 1}</p>
                <p className="font-serif text-lg text-charcoal">{region}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA - THIS IS YOUR SISTERHOOD */}
      <section className="py-24 lg:py-36 px-4 sm:px-6 lg:px-8 bg-burgundy text-ivory relative overflow-hidden" data-testid="final-cta-section">
        <div className="max-w-4xl mx-auto text-center space-y-10 relative">
          <p className="label-editorial text-terracotta">In closing</p>
          <h2 className="font-serif text-5xl md:text-6xl lg:text-8xl leading-[0.95]">
            This is<br/>your <em className="not-italic text-terracotta">sisterhood</em>.
          </h2>
          <div className="space-y-2 text-lg md:text-xl text-ivory/80 font-serif italic">
            <p>Your story.</p>
            <p>Your questions.</p>
            <p>Your ideas.</p>
            <p>Your ambitions.</p>
            <p>Your voice.</p>
          </div>
          <div className="hairline w-24 mx-auto bg-terracotta"></div>
          <p className="label-editorial text-terracotta">Sisters Empowering Sisters</p>
          <Link to="/register" className="inline-flex items-center gap-2 px-10 py-5 bg-ivory text-charcoal hover:bg-beige transition-colors font-medium text-sm uppercase tracking-widest" data-testid="final-join-button">
            Join Behenchara <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Homepage;
